import type { AxiosInstance } from 'axios'

import type {
  Grouping,
  CustomGroup,
  GroupingCapacityResponse,
  GroupingCreatePayload,
  GroupingUpdatePayload,
} from '../models/grouping.types'
import type { ConditionalMetric, CustomMetric } from '../models/metric.types'

import { DEFINITIONS_REQUEST_CONFIG } from './config'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

export class DefinitionsMetadataApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  async listCustomMetrics(workspaceId: number | string): Promise<CustomMetric[]> {
    return (
      unwrapEnvelope(
        await this.http.get<ApiEnvelope<CustomMetric[]>>(
          `/custom_metric_formulas?workspace_id=${workspaceId}`,
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ) ?? []
    )
  }

  async createCustomMetric(metric: CustomMetric): Promise<unknown> {
    return unwrap(
      await this.http.post('/create_custom_formula_request', metric, DEFINITIONS_REQUEST_CONFIG),
    )
  }

  async updateCustomMetric(metric: CustomMetric): Promise<unknown> {
    return unwrap(
      await this.http.put('/update_custom_formula_request', metric, DEFINITIONS_REQUEST_CONFIG),
    )
  }

  async deleteCustomMetric(id: number | string): Promise<unknown> {
    return unwrap(
      await this.http.delete(`/custom-metrics/${id}/delete`, DEFINITIONS_REQUEST_CONFIG),
    )
  }

  private conditionalBase(accountId: number | string, workspaceId: number | string): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/metadata/metrics/conditional/`
  }

  async listConditionalMetrics(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<ConditionalMetric[]> {
    return (
      unwrap(
        await this.http.get<ConditionalMetric[]>(
          this.conditionalBase(accountId, workspaceId),
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ) ?? []
    )
  }

  async createConditionalMetric(
    accountId: number | string,
    workspaceId: number | string,
    metric: ConditionalMetric,
  ): Promise<unknown> {
    return unwrap(
      await this.http.post(
        this.conditionalBase(accountId, workspaceId),
        metric,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async updateConditionalMetric(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
    metric: ConditionalMetric,
  ): Promise<unknown> {
    return unwrap(
      await this.http.put(
        `${this.conditionalBase(accountId, workspaceId)}${id}/`,
        metric,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  /** Legacy wire contract: deletion is a bodyless PUT, not DELETE. */
  async deleteConditionalMetric(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
  ): Promise<unknown> {
    return unwrap(
      await this.http.put(
        `${this.conditionalBase(accountId, workspaceId)}${id}/`,
        undefined,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  private groupingBase(accountId: number | string, workspaceId: number | string): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/metadata/groupings/`
  }

  /**
   * Per-entity grouping slot capacity (BE-30-09 / ORP-20374, backend `metadata/grouping/views.py`).
   *
   * Read-only and derived from configuration, so it is safe to call once per workspace load. The
   * shape is deliberately explicit about the no-config case: an entity ABSENT from `capacities`
   * has no configuration row and is therefore UNBOUNDED server-side — `default_capacity: null`
   * says so, and a client must never read absence as zero.
   */
  async groupingCapacities(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<GroupingCapacityResponse | null> {
    return unwrap(
      await this.http.get<GroupingCapacityResponse>(
        `${this.groupingBase(accountId, workspaceId)}config/`,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async listGroupings(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<Grouping[]> {
    return (
      unwrap(
        await this.http.get<Grouping[]>(
          this.groupingBase(accountId, workspaceId),
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ) ?? []
    )
  }

  async createGrouping(
    accountId: number | string,
    workspaceId: number | string,
    grouping: GroupingCreatePayload,
  ): Promise<{ id?: number | string } | null> {
    return unwrap(
      await this.http.post<{ id?: number | string } | null>(
        this.groupingBase(accountId, workspaceId),
        grouping,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async updateGrouping(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
    grouping: GroupingUpdatePayload,
  ): Promise<unknown> {
    return unwrap(
      await this.http.patch(
        `${this.groupingBase(accountId, workspaceId)}${id}/`,
        grouping,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async deleteGrouping(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
  ): Promise<unknown> {
    return unwrap(
      await this.http.delete(
        `${this.groupingBase(accountId, workspaceId)}${id}/`,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async createCustomGroup(
    accountId: number | string,
    workspaceId: number | string,
    groupingId: number | string,
    group: CustomGroup,
  ): Promise<unknown> {
    return unwrap(
      await this.http.post(
        `${this.groupingBase(accountId, workspaceId)}${groupingId}/custom-groups/`,
        group,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  private customGroupPath(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
  ): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/metadata/custom-groups/${id}/`
  }

  async updateCustomGroup(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
    group: CustomGroup,
  ): Promise<unknown> {
    return unwrap(
      await this.http.patch(
        this.customGroupPath(accountId, workspaceId, id),
        group,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async deleteCustomGroup(
    accountId: number | string,
    workspaceId: number | string,
    id: number | string,
  ): Promise<unknown> {
    return unwrap(
      await this.http.delete(
        this.customGroupPath(accountId, workspaceId, id),
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }
}

export const definitionsMetadataApi = new DefinitionsMetadataApi()
